import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Checkbox, IconButton, Select, MenuItem, Box, Typography
} from '@material-ui/core';
import {
  Delete as DeleteIcon, PictureAsPdf as PdfIcon, Close as CloseIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  DragIndicator as DragIndicatorIcon
} from '@material-ui/icons';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    }
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '20px 24px',
    '& h2': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '1.5rem',
      fontWeight: 600,
    }
  },
  closeButton: {
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    }
  },
  tableContainer: {
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    '& .MuiTableCell-stickyHeader': {
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #dee2e6',
      zIndex: 2,
    }
  },
  tableHead: {
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: '#495057',
      padding: '16px 12px',
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f8f9fa',
      transition: 'background-color 0.2s ease',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#fafbfc',
    }
  },
  // ✅ NOUVEAU : ligne en cours de glissement
  draggingRow: {
    opacity: 0.4,
    backgroundColor: '#e8eaf6 !important',
  },
  // ✅ NOUVEAU : ligne survolée pendant le glissement
  dragOverRow: {
    backgroundColor: '#ede7f6 !important',
    borderTop: '3px solid #667eea',
  },
  tableCell: {
    padding: '12px',
    borderBottom: '1px solid #e9ecef',
  },
  // ✅ NOUVEAU : style de la poignée de drag
  dragHandle: {
    cursor: 'grab',
    color: '#adb5bd',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      color: '#495057',
    },
    '&:active': {
      cursor: 'grabbing',
    }
  },
  textField: {
    '& .MuiInputBase-root': {
      fontSize: '0.875rem',
      backgroundColor: 'white',
      borderRadius: 6,
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
      '&.Mui-focused': {
        backgroundColor: 'white',
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dee2e6',
    },
  },
  select: {
    '& .MuiSelect-root': {
      padding: '10px 12px',
      fontSize: '0.875rem',
      backgroundColor: 'white',
      borderRadius: 6,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dee2e6',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#adb5bd',
    }
  },
  checkbox: {
    '&.Mui-checked': {
      color: '#667eea',
    }
  },
  deleteButton: {
    color: '#dc3545',
    '&:hover': {
      backgroundColor: 'rgba(220, 53, 69, 0.08)',
    }
  },
  dialogActions: {
    padding: '20px 24px',
    borderTop: '1px solid #e9ecef',
    gap: '12px',
  },
  cancelButton: {
    borderRadius: 8,
    padding: '8px 24px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
  },
  saveButton: {
    borderRadius: 8,
    padding: '8px 32px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)',
    }
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  }
}));

const mergeVICData = (appointments, vicList, savedVicData, dossierValue) => {
  const uniqueEntries = new Map();

  if (savedVicData && savedVicData.length > 0) {
    savedVicData.forEach(saved => {
      const key = `${saved.lot.trim().toUpperCase()}-${saved.entreprise.trim().toUpperCase()}`;
      uniqueEntries.set(key, {
        id: saved.id || `saved-${key}`,
        dossier: dossierValue,
        entreprise: saved.entreprise,
        lot: saved.lot,
        intervention_date: saved.intervention_date || '',
        rdv_date: saved.rdv_date || '',
        ppsps: saved.ppsps,
        vic: saved.vic,
        observation: saved.observation || '',
        source: 'saved'
      });
    });
  }

  const vicAppointments = appointments.filter(
    apt => apt.type && apt.type.trim().toLowerCase().includes('vic')
  );

  vicAppointments.forEach(apt => {
    const key = `${apt.lot.trim().toUpperCase()}-${apt.company.trim().toUpperCase()}`;
    if (uniqueEntries.has(key)) {
      const existing = uniqueEntries.get(key);
      uniqueEntries.set(key, {
        ...existing,
        intervention_date: apt.intervention_date || existing.intervention_date,
        rdv_date: apt.appointment_start || existing.rdv_date,
        ppsps: apt.ppsps === 1 || existing.ppsps,
        source: 'merged'
      });
    } else {
      uniqueEntries.set(key, {
        id: apt.id,
        dossier: dossierValue,
        entreprise: apt.company || '',
        lot: apt.lot || '',
        intervention_date: apt.intervention_date || '',
        rdv_date: apt.appointment_start || '',
        ppsps: apt.ppsps === 1,
        vic: false,
        observation: '',
        source: 'appointment'
      });
    }
  });

  vicList.forEach((vic, index) => {
    const key = `${vic.lot.trim().toUpperCase()}-${vic.company.trim().toUpperCase()}`;
    if (uniqueEntries.has(key)) {
      const existing = uniqueEntries.get(key);
      existing.vic = true;
    } else {
      uniqueEntries.set(key, {
        id: `vic-${index}`,
        dossier: dossierValue,
        entreprise: vic.company,
        lot: vic.lot,
        intervention_date: '',
        rdv_date: '',
        ppsps: vic.ppsps,
        vic: true,
        observation: '',
        source: 'vicList'
      });
    }
  });

  return Array.from(uniqueEntries.values()).sort((a, b) => {
    const interventionDateCompare = a.intervention_date.localeCompare(b.intervention_date);
    if (interventionDateCompare !== 0) return interventionDateCompare;
    return a.entreprise.localeCompare(b.entreprise);
  });
};

// ─────────────────────────────────────────────
// Composant Popup avec Drag & Drop
// ─────────────────────────────────────────────
const VICTrackingPopup = ({ open, onClose, appointments, vicList, savedVicData, dossierValue, lotList, onSave }) => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);

  // ✅ Refs pour le drag & drop (pas de re-render inutile)
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  React.useEffect(() => {
    if (open) {
      const mergedData = mergeVICData(appointments, vicList, savedVicData || [], dossierValue);
      setRows(mergedData);
    }
  }, [open, appointments, vicList, savedVicData, dossierValue]);

  // ✅ Handlers du Drag & Drop
  const handleDragStart = (e, index) => {
    dragIndex.current = index;
    // Rend le ghost de drag plus léger
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) {
      setDragOverIndex(null);
      return;
    }
    const newRows = [...rows];
    const [draggedRow] = newRows.splice(dragIndex.current, 1);
    newRows.splice(index, 0, draggedRow);
    setRows(newRows);
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleFieldChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const handleDeleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
  };

  const handleSave = () => {
    onSave(rows);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className={classes.dialog}
      PaperProps={{
        style: { minHeight: '85vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h5" component="span" style={{ fontWeight: 600 }}>
            📋 Tableau de suivi des VIC
          </Typography>
          <IconButton onClick={onClose} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ padding: '24px', backgroundColor: '#f8f9fa' }}>
        <TableContainer
          component={Paper}
          className={classes.tableContainer}
          style={{ maxHeight: 'calc(90vh - 250px)' }}
        >
          <Table stickyHeader>
            <TableHead className={classes.tableHead}>
              <TableRow>
                {/* ✅ Colonne poignée de drag */}
                <TableCell style={{ width: '40px' }} />
                <TableCell align="center" style={{ width: '100px' }}>Dossier</TableCell>
                <TableCell align="center" style={{ minWidth: '180px' }}>Entreprise</TableCell>
                <TableCell align="center" style={{ minWidth: '150px' }}>Lot</TableCell>
                <TableCell style={{ minWidth: '160px' }}>Date Intervention</TableCell>
                <TableCell align="center" style={{ minWidth: '160px' }}>Date RDV</TableCell>
                <TableCell align="center" style={{ width: '90px' }}>PPSPS</TableCell>
                <TableCell align="center" style={{ width: '90px' }}>VIC</TableCell>
                <TableCell style={{ minWidth: '220px' }}>Observation</TableCell>
                <TableCell align="center" style={{ width: '80px' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`
                    ${classes.tableRow}
                    ${dragIndex.current === index ? classes.draggingRow : ''}
                    ${dragOverIndex === index && dragIndex.current !== index ? classes.dragOverRow : ''}
                  `}
                  style={{ userSelect: 'none' }}
                >
                  <TableCell className={classes.tableCell} style={{ padding: '0 8px' }}>
                    <span className={classes.dragHandle} title="Glisser pour réordonner">
                      <DragIndicatorIcon fontSize="small" />
                    </span>
                  </TableCell>

                  <TableCell className={classes.tableCell} align="center">
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#495057' }}>
                      {row.dossier}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#495057' }}>
                      {row.entreprise}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Select
                      value={row.lot}
                      onChange={(e) => handleFieldChange(index, 'lot', e.target.value)}
                      size="small"
                      fullWidth
                      variant="outlined"
                      className={classes.select}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Sélectionner un lot</em>
                      </MenuItem>
                      {lotList.map((lot, i) => (
                        <MenuItem key={i} value={lot}>{lot}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      type="date"
                      value={row.intervention_date}
                      onChange={(e) => handleFieldChange(index, 'intervention_date', e.target.value)}
                      size="small"
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      type="date"
                      value={row.rdv_date ? moment(row.rdv_date).format('YYYY-MM-DD') : ''}
                      onChange={(e) => handleFieldChange(index, 'rdv_date', e.target.value)}
                      size="small"
                      fullWidth
                      variant="outlined"
                      className={classes.textField}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    <Checkbox
                      checked={row.ppsps}
                      onChange={(e) => handleFieldChange(index, 'ppsps', e.target.checked)}
                      className={classes.checkbox}
                      icon={<CancelIcon style={{ color: '#dee2e6' }} />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    <Checkbox
                      checked={row.vic}
                      onChange={(e) => handleFieldChange(index, 'vic', e.target.checked)}
                      className={classes.checkbox}
                      icon={<CancelIcon style={{ color: '#dee2e6' }} />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      value={row.observation}
                      onChange={(e) => handleFieldChange(index, 'observation', e.target.value)}
                      size="small"
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={2}
                      placeholder="Ajouter une observation..."
                      className={classes.textField}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    <IconButton
                      onClick={() => handleDeleteRow(index)}
                      size="small"
                      className={classes.deleteButton}
                      title="Supprimer la ligne"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="textSecondary">
            Total: <strong>{rows.length}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600, margin: '0 10px' }}>•</Typography>
          <Typography variant="body2" color="textSecondary">
            VIC réalisées: <strong>{rows.filter(r => r.vic).length}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600, margin: '0 10px' }}>•</Typography>
          <Typography variant="body2" color="textSecondary">
            PPSPS reçus: <strong>{rows.filter(r => r.ppsps).length}</strong>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleSave}
          variant="contained"
          className={classes.saveButton}
          startIcon={<PdfIcon />}
        >
          GÉNÉRER LE PDF
        </Button>
        <Button onClick={onClose} variant="outlined" className={classes.cancelButton}>
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VICTrackingPopup;